import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FacebookIcon from "@mui/icons-material/Facebook";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import {
  MODULE_DETAILS,
  MODULE_PRICING,
  isModuleAvailable,
} from "../constants/moduleSubscriptionCatalog";
import PublicMarketingShell from "../components/PublicMarketingShell";
import {
  PM_COLORS,
  publicAccentTextSx,
  publicCardSx,
  publicHeroChipSx,
  publicSubtitleSx,
  publicTitleSx,
} from "../constants/publicMarketingTheme";

const FACEBOOK_PAGE_URL =
  "https://www.facebook.com/profile.php?id=61573253949524";

const availableModules = MODULE_PRICING.filter(isModuleAvailable);
const roadmapModules = MODULE_PRICING.filter((m) => !isModuleAvailable(m));
const pricingMin = Math.min(...availableModules.map((m) => m.price));
const pricingMax = Math.max(...availableModules.map((m) => m.price));

const features = [
  [
    "Unified Operations",
    `${availableModules.map((m) => m.label).join(", ")}.${
      roadmapModules.length
        ? ` On the roadmap: ${roadmapModules.map((m) => m.label).join(", ")}.`
        : ""
    } One secure platform aligned with how you subscribe today.`,
  ],
  [
    "Module-Based Subscription",
    `Subscribe per module (reference from about PHP ${pricingMin.toLocaleString(
      "en-PH",
    )}–${pricingMax.toLocaleString(
      "en-PH",
    )} / month each at list rates). Expand when you are ready—see Pricing for the full module grid.`,
  ],
  [
    "Cross-Module Automation",
    `${MODULE_DETAILS.Accounting.functionalities[2]} ${MODULE_DETAILS.Procurement.functionalities[3]} ${MODULE_DETAILS.Sales.functionalities[3]}`,
  ],
  [
    "Role-Based Security",
    "Organization and module role controls keep access scoped and secure.",
  ],
  [
    "Realtime Visibility",
    `${MODULE_DETAILS.Accounting.functionalities[5]} Dashboards and lists refresh as your team works.`,
  ],
  [
    "Depth You Can Use",
    `Examples: ${MODULE_DETAILS.HR.functionalities[0]}; ${MODULE_DETAILS.Inventory.functionalities[2]}; ${MODULE_DETAILS.Marketing.functionalities[1]}.`,
  ],
];

const stories = [
  {
    role: "Finance Manager",
    challenge: "Manual reconciliation and duplicate entry.",
    result:
      "Reduced monthly close effort after centralizing accounting operations.",
  },
  {
    role: "Operations Lead",
    challenge: "Procurement and accounting disconnected approvals.",
    result:
      "Improved control through automated PO-to-Bill and journal posting.",
  },
  {
    role: "HR Director",
    challenge: "Late payroll visibility for stakeholders.",
    result: "Higher confidence with integrated HR and accounting flows.",
  },
];

const stats = [
  { label: "Organizations onboarded", value: "120+" },
  { label: "Automated postings / month", value: "20k+" },
  { label: "Average process time reduced", value: "38%" },
  { label: "Uptime target", value: "99.9%" },
];

const faqs = [
  [
    "Can we start with only a few modules?",
    "Yes. Subscriptions are module-based so you can begin lean and expand anytime.",
  ],
  [
    "Does it support role-based permissions?",
    "Yes. Organization-level and module-level role permissions are supported.",
  ],
  [
    "Are cross-module automations included?",
    "Yes. When the related modules are active—e.g. Procurement with Accounting, or Sales with Accounting—in-app flows create the downstream bills, invoices, journal entries, and inventory movements described in each module’s catalog entry.",
  ],
  [
    "What about Production or Point of Sale?",
    `${roadmapModules.map((m) => m.label).join(" and ")} appear in the catalog for future pricing; they are not selectable subscriptions yet.`,
  ],
];

const cardSx = {
  ...publicCardSx,
  p: 3,
  borderRadius: 4,
};

const LandingPage = () => {
  const navigate = useNavigate();
  const user = useUser();

  return (
    <PublicMarketingShell
      sx={{
        "@keyframes floatCard": {
          "0%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-6px)" },
          "100%": { transform: "translateY(0px)" },
        },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            mt: 2,
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "center",
            animation: "fadeUp 500ms ease-out",
          }}
        >
          <Box sx={{ flex: "1 1 480px", minWidth: 0 }}>
            <Chip
              label="Unified ERP for growth-focused teams"
              sx={publicHeroChipSx}
            />
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 800,
                color: PM_COLORS.textPrimary,
                lineHeight: 1.1,
                fontSize: { xs: 32, sm: 42, md: 52 },
              }}
            >
              Run your whole business
              <Box component="span" sx={publicAccentTextSx}>
                {" "}
                from one ERP
              </Box>
              .
            </Typography>
            <Typography sx={{ ...publicSubtitleSx, mt: 2, maxWidth: 620 }}>
              JD Integrations delivers the same module surface you choose at
              sign-up—general ledger through payments, HR through payroll,
              procurement and goods receipts, inventory and sales, plus marketing
              pipeline—without forcing a monolithic bundle.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              {!user ? (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                    onClick={() => navigate("/signup")}
                  >
                    Get Started
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="large"
                    sx={{ borderRadius: 2 }}
                    onClick={() => navigate("/login")}
                  >
                    Log In
                  </Button>
                  <Button
                    variant="text"
                    color="secondary"
                    size="large"
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                    onClick={() => navigate("/pricing")}
                  >
                    Module pricing
                  </Button>
                  <Button
                    variant="text"
                    color="secondary"
                    size="large"
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                    onClick={() => navigate("/integrations")}
                  >
                    How modules connect
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/dashboard")}
                >
                  Go to Dashboard
                </Button>
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{ color: "rgba(148,163,184,0.9)", mt: 1.5, display: "block" }}
            >
              Secure by design - Role-based access - Subscription-ready modules
            </Typography>
            <Link
              href={FACEBOOK_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                mt: 1.75,
                color: "rgba(147,197,253,0.98)",
                fontWeight: 600,
                fontSize: "0.9rem",
              }}
            >
              <FacebookIcon sx={{ fontSize: 22 }} aria-hidden />
              Follow us on Facebook
            </Link>
          </Box>

          <Box sx={{ flex: "1 1 420px", minWidth: 0 }}>
            <Paper
              elevation={10}
              sx={{
                ...cardSx,
                background:
                  "linear-gradient(145deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.92) 60%, rgba(30,64,175,0.8) 100%)",
                animation: "floatCard 4s ease-in-out infinite",
                boxShadow: "0 14px 28px rgba(0,0,0,0.3)",
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
                LIVE PREVIEW
              </Typography>
              <Typography
                component="h3"
                variant="h6"
                sx={{ fontWeight: 700, mb: 1.5 }}
              >
                Today&apos;s organization snapshot
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Box
                  sx={{
                    ...cardSx,
                    p: 2,
                    flex: "1 1 200px",
                    borderColor: "rgba(45,212,191,0.4)",
                    backgroundColor: "rgba(15,118,110,0.18)",
                  }}
                >
                  <Typography variant="caption">Subscribable modules</Typography>
                  <Typography
                    component="p"
                    variant="h5"
                    sx={{ fontWeight: 800, ...publicAccentTextSx, mt: 0.4 }}
                  >
                    {availableModules.length} live · {roadmapModules.length}{" "}
                    roadmap
                  </Typography>
                </Box>
                <Box
                  sx={{
                    ...cardSx,
                    p: 2,
                    flex: "1 1 200px",
                    borderColor: "rgba(129,140,248,0.5)",
                    backgroundColor: "rgba(30,64,175,0.2)",
                  }}
                >
                  <Typography variant="caption">
                    This Month&apos;s Activity
                  </Typography>
                  <Typography
                    component="p"
                    variant="h5"
                    sx={{ fontWeight: 800, color: "#a5b4fc", mt: 0.4 }}
                  >
                    312
                  </Typography>
                </Box>
                <Box sx={{ ...cardSx, p: 2, flex: "1 1 100%" }}>
                  <Typography variant="caption">
                    Cross-module automation
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.4 }}>
                    Procurement ties to Core suppliers and Accounting bills;
                    goods receipts and purchase requests pair with Inventory when
                    both are on; Sales paths toward invoices and journals when
                    Accounting is enabled—matching the integration story in your
                    module catalog.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            animation: "fadeUp 800ms ease-out",
          }}
        >
          {features.map(([title, desc]) => (
            <Box key={title} sx={{ flex: "1 1 420px", minWidth: 0 }}>
              <Paper
                sx={{
                  ...cardSx,
                  p: 2.2,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 10px 20px rgba(0,0,0,0.25)",
                  },
                }}
              >
                <Typography
                  component="h3"
                  variant="h6"
                  sx={{ fontWeight: 700, mb: 0.8 }}
                >
                  {title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.92 }}>
                  {desc}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        <Paper sx={{ ...cardSx, mt: 3, animation: "fadeUp 1000ms ease-out" }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Module Ecosystem
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
            Activate only what you need now, then expand as your team grows.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
            {MODULE_PRICING.map((m) => (
              <Chip
                key={m.key}
                label={
                  isModuleAvailable(m)
                    ? m.label
                    : `${m.label} (roadmap)`
                }
                color="secondary"
                variant={isModuleAvailable(m) ? "filled" : "outlined"}
                sx={{
                  borderLeft: `3px solid ${m.color}`,
                  fontWeight: isModuleAvailable(m) ? 600 : 500,
                }}
              />
            ))}
          </Stack>
        </Paper>

        <Box
          sx={{
            mt: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            animation: "fadeUp 1125ms ease-out",
          }}
        >
          <Box sx={{ flex: "1 1 520px", minWidth: 0 }}>
            <Paper sx={cardSx}>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 800, mb: 1.2 }}>
                How it works
              </Typography>
              <Stack spacing={1.1}>
                <Typography variant="body2">
                  1. Choose your module subscription bundle.
                </Typography>
                <Typography variant="body2">
                  2. Register your organization and owner account.
                </Typography>
                <Typography variant="body2">
                  3. Assign roles and permissions per team.
                </Typography>
                <Typography variant="body2">
                  4. Operate with real-time data and automated cross-module
                  flow.
                </Typography>
              </Stack>
            </Paper>
          </Box>
          <Box sx={{ flex: "1 1 360px", minWidth: 0 }}>
            <Paper sx={cardSx}>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 800, mb: 1.2 }}>
                Trusted by Teams
              </Typography>
              <Stack spacing={1.6}>
                {stories.slice(0, 2).map((s) => (
                  <Box key={s.role}>
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", opacity: 0.92 }}
                    >
                      "{s.result}"
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#86efac" }}>
                      {s.role}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            animation: "fadeUp 1225ms ease-out",
          }}
        >
          {stats.map((s) => (
            <Box key={s.label} sx={{ flex: "1 1 220px", minWidth: 0 }}>
              <Paper sx={{ ...cardSx, p: 2, textAlign: "center" }}>
                <Typography
                  component="p"
                  variant="h5"
                  sx={{ fontWeight: 800, ...publicAccentTextSx }}
                >
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {s.label}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>

        <Paper sx={{ ...cardSx, mt: 3, animation: "fadeUp 1275ms ease-out" }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
            Customer Stories
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {stories.map((s) => (
              <Box key={s.role} sx={{ flex: "1 1 300px", minWidth: 0 }}>
                <Paper sx={{ ...cardSx, p: 2 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#93c5fd", display: "block", mb: 0.4 }}
                  >
                    Challenge
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    {s.challenge}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#86efac", display: "block", mb: 0.4 }}
                  >
                    Result
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", opacity: 0.95 }}
                  >
                    "{s.result}"
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#86efac", mt: 0.5, display: "block" }}
                  >
                    {s.role}
                  </Typography>
                </Paper>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper sx={{ ...cardSx, mt: 3, animation: "fadeUp 1325ms ease-out" }}>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 800, mb: 1.3 }}>
            Frequently Asked Questions
          </Typography>
          {faqs.map(([q, a]) => (
            <Accordion
              key={q}
              disableGutters
              sx={{
                backgroundColor: "transparent",
                color: "inherit",
                borderBottom: "1px solid rgba(148,163,184,0.22)",
                boxShadow: "none",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#e2e8f0" }} />}
              >
                <Typography sx={{ fontWeight: 600 }}>{q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        <Paper
          sx={{
            ...cardSx,
            mt: 3,
            border: "1px solid rgba(148,163,184,0.38)",
            background:
              "linear-gradient(130deg, rgba(2,6,23,0.96) 0%, rgba(15,23,42,0.95) 55%, rgba(30,41,59,0.9) 100%)",
            boxShadow: "0 14px 30px rgba(0,0,0,0.35)",
            animation: "fadeUp 1400ms ease-out",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box>
              <Typography component="h2" variant="h5" sx={{ fontWeight: 800 }}>
                Ready to modernize your operations?
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.4 }}>
                Start with the modules you need and expand with confidence.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              {!user ? (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                    onClick={() => navigate("/signup")}
                  >
                    Start Free Setup
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate("/login")}
                  >
                    Log In
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ fontWeight: 700 }}
                  onClick={() => navigate("/dashboard")}
                >
                  Open Dashboard
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Divider sx={{ my: 3, borderColor: "rgba(148,163,184,0.25)" }} />
        <Stack
          spacing={1.5}
          alignItems="center"
          sx={{ pb: 2, textAlign: "center" }}
        >
          <Typography
            variant="caption"
            sx={{ color: "rgba(148,163,184,0.9)", maxWidth: 560 }}
          >
            JD Integrations ERP — Built for secure multi-organization workflows —
            scalable module subscriptions.
          </Typography>
          <Link
            href={FACEBOOK_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              color: "#93c5fd",
              fontWeight: 600,
            }}
          >
            <FacebookIcon sx={{ fontSize: 22 }} aria-hidden />
            JD Integrations on Facebook
          </Link>
        </Stack>
      </Container>
    </PublicMarketingShell>
  );
};

export default LandingPage;
